import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import TableContainer from "../../Components/Common/TableContainer";
import { Col, Row, Card, CardBody } from "reactstrap";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import { toast, ToastContainer } from "react-toastify";
import {
  getDemoClassRequests,
  deleteRequest,
  updateDemoStatus,
} from "utils/apiService";
import DeleteModal from "Components/Common/DeleteModal";
import { RejectModal } from "Components/Common/RejectModal";

function DemoClassReq() {
  document.title = "Demo Class Requests";
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await getDemoClassRequests();
      if (res.success) {
        setRequests(res.response.demoClasses || []);
      } else {
        toast.error("Failed to fetch demo class requests.");
      }
    } catch (err) {
      console.error("Error fetching demo class requests:", err);
      toast.error("Something went wrong!");
    }
  };

  const handleWhatsApp = (phoneNumber: string) => {
    if (phoneNumber) {
      const formattedNumber = phoneNumber.replace(/\D/g, "");
      window.open(`https://wa.me/${formattedNumber}`, "_blank");
    } else {
      alert("Phone number not available");
    }
  };

  const handleApprove = async (id: any) => {
    const result = await updateDemoStatus({
      isApproved: true,
      id,
    });
    if (result.success) {
      toast.success("Status Updated Successfully!");
      fetchRequests();
    }
  };

  const handleReject = async (request: any) => {
    setSelectedRequest(request);
    setRejectionReason("");
    setRejectModal(true);
  };

  const confirmReject = async () => {
    try {
      if (selectedRequest) {
        const result = await updateDemoStatus({
          isApproved: false,
          id: (selectedRequest as { id: number }).id,
          reason: rejectionReason,
        });
        if (result.success) {
          toast.success("Request rejected successfully!");
          fetchRequests();
        } else {
          toast.error("Failed to reject the request.");
        }
      }
    } catch (error) {
      toast.error("Failed to reject the request.");
    } finally {
      setRejectModal(false);
      setSelectedRequest(null);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRejectionReason(e.target.value);
  };

  const handleDelete = async (request: any) => {
    setSelectedRequest(request);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (selectedRequest) {
        await deleteRequest((selectedRequest as { id: number }).id);
        toast.success("Demo class request deleted successfully!");
        fetchRequests();
      }
    } catch (error) {
      toast.error("Failed to delete the request.");
      console.error("Delete Error:", error);
    } finally {
      setDeleteModal(false);
      setSelectedRequest(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        cell: (cellProps) => {
          // Adding 1 because array index starts from 0
          return cellProps.row.index + 1;
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Full Name",
        accessorKey: "name",
      },
      {
        header: "Email Address",
        accessorKey: "email",
      },
      {
        header: "Phone Number",
        accessorKey: "phone_number",
        cell: ({ row }) => {
          const phoneWithCode = row.original.phone_number || "";
          return phoneWithCode.replace(/-/g, " ").replace(/[\(\)]/g, " ");
        },
      },
      {
        header: "Course",
        accessorKey: "course",
      },
      {
        header: "Subject",
        accessorKey: "subject",
      },
      {
        header: "Preferred Time Slot",
        accessorKey: "time_slot",
      },
      {
        header: "Education Level",
        accessorKey: "education",
      },
      {
        header: "Country",
        accessorKey: "country",
      },
      {
        header: "Message",
        accessorKey: "message",
      },
      {
        header: "Status",
        accessorKey: "status",
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const request = cellProps.row.original;
          return (
            <div className="d-flex gap-3">
              {(!request?.status ||
                request.status.toLowerCase() === "pending") && (
                <>
                  <Link
                    to="#"
                    className="text-success mx-2"
                    onClick={() => handleApprove(request.id)}
                  >
                    <i className="mdi mdi-check-circle font-size-18" />
                  </Link>

                  <Link
                    to="#"
                    className="text-danger"
                    onClick={() => handleReject(request)}
                  >
                    <i className="mdi mdi-close-circle font-size-18" />
                  </Link>
                </>
              )}

              <Link
                to="#"
                className="text-success"
                onClick={() => handleWhatsApp(request.phone_number)}
              >
                <i className="mdi mdi-whatsapp font-size-18" />
              </Link>
              <Link
                to="#"
                className="text-danger"
                onClick={() => handleDelete(request)}
              >
                <i className="mdi mdi-delete font-size-18" />
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={confirmDelete}
        onCloseClick={() => setDeleteModal(false)}
        message="Are you sure you want to delete this demo class request? You won't be able to revert this!"
      />
      <RejectModal
        show={rejectModal}
        onRejectClick={confirmReject}
        onCloseClick={() => setRejectModal(false)}
        onChange={handleReasonChange}
        reason={rejectionReason}
        title="Reject Demo Class Request"
        message="Are you sure you want to reject this demo class request? This action cannot be undone."
        rejectButtonText="Reject Request"
        reasonPlaceholder="Please provide a reason for rejecting this request"
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs breadcrumbItem="Demo Class Requests" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={requests}
                    isGlobalFilter={false}
                    isAddButton={false}
                    customPageSize={10}
                    isPagination={true}
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100"
                    theadClass="table-light"
                    pagination="pagination"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
}

export default DemoClassReq;
